import { HttpEvent, HttpHandler, HttpRequest, HttpInterceptor, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication.service';
import { AuthResponse } from '../../models/AuthResponse';
import { CommonValues } from '../../static/CommonValues';
import { CommonFunctions } from '../../static/common-functions';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class LegacyTokenAuthorizationInterceptor implements HttpInterceptor {

  constructor(private authService: AuthenticationService) {
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.url.indexOf(environment.APIEndPoint_v2) === -1 && request.url.indexOf(environment.APICEndPoint) === -1) {
      return next.handle(request);
    }
    const legacyTokenResponseJson = localStorage.getItem(CommonValues.StorageKeys_LegacyToken);
    if (legacyTokenResponseJson) {
      const legacyTokenResponse: AuthResponse = JSON.parse(legacyTokenResponseJson);
      const nowInSeconds = Math.floor(Date.now() / 1000) - 60;
      if (legacyTokenResponse.expiresAtSeconds < nowInSeconds || !legacyTokenResponse.token) { //Expired token
        return this.fetchLegacyToken(request, next);
      } else {
        if (environment.debug) {
          console.debug(`%c Reading legacy token from cache. ${request.urlWithParams}`, CommonValues.LoggingMessage_ColorGreen);
        }
        let headers = request.headers ? request.headers : new HttpHeaders();
        headers = CommonFunctions.setAuthorizationHeader(headers, legacyTokenResponse.token);
        const newRequest = request.clone({ headers });
        return next.handle(newRequest);
      }
    } else {
      return this.fetchLegacyToken(request, next);
    }
  }

  private fetchLegacyToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    localStorage.removeItem(CommonValues.StorageKeys_LegacyToken);
    if (environment.debug) {
      console.debug(`%c Fetching legacy token. Request: ${request.urlWithParams}`, CommonValues.LoggingMessage_ColorGreen);
    }
    return this.authService.getLegacyToken().pipe(
      switchMap((tokenResponse: AuthResponse) => {
        let headers = request.headers ? request.headers : new HttpHeaders();
        if (!tokenResponse?.token) {
          //NOTE: [dturco 05.22.2025] this should never get hit, as we return a 403, but just as a failsafe
          console.error('Legacy token is empty or undefined: ', tokenResponse.errorMessage ?? "Unauthorized", request.urlWithParams);
          const err = new Error(tokenResponse.errorMessage ?? "Unauthorized") as any;
          err.isUnauthorized = true;
          err.status = 403;
          return throwError(() => err);
        } else {
          localStorage.setItem(CommonValues.StorageKeys_LegacyToken, JSON.stringify(tokenResponse));
          headers = CommonFunctions.setAuthorizationHeader(headers, tokenResponse.token);
        }
        const newRequest = request.clone({ headers });
        return next.handle(newRequest);
      })
    );
  }
}

